// src/settings/Users.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Alert,
    Switch,
    Tooltip
} from '@mui/material';
import { 
    Delete, 
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Key as KeyIcon,
    CheckCircle,
    Cancel,
    Store as StoreIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIUserStore,
    APIUserList,
    APIUserUpdate,
    APIUserDelete,
    APIUserUpdatePassword,
    APIBranchNames,
} from '../../apiServices/APIs';
import { formatCreatedAtDate, formatNumberWithComma, getUserDetails, recordsPerPage, statusData } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { TEXT_CONNECTION_ERROR } from '../../apiServices/Text';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Roles strictly aligned with the schema and backend definitions ('AD' - Admin, 'ST' - Staff)
const localRoleOptions = [
    { id: 'AD', name: 'Admin' },
    { id: 'ST', name: 'Staff' }
];

const Users = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [error, setError] = useState(null);
    const [branchesError, setBranchesError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [branches, setBranches] = useState([]);
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Form data fields corresponding directly to database table
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: '',
        branch_id: '',
        password: '',
        confirmPassword: '',
        status: '1'
    });

    const [passwordFormData, setPasswordFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        email: '',
        role: '',
        branch_id: '',
        status: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        role: false,
        branch_id: false,
        password: false,
        confirmPassword: false,
        status: false
    });

    const [passwordErrors, setPasswordErrors] = useState({
        password: false,
        confirmPassword: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openChangePassword, setOpenChangePassword] = useState(false);

    // Permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    useEffect(() => {
        fetchData();
        fetchBranches();
    }, []);

    // Function to get role name from role ID
    const getRoleName = (roleId) => {
        if (!roleId) return "Not Set";
        const role = localRoleOptions.find(item => item.id === roleId);
        return role ? role.name : roleId;
    };

    // Function to get role color
    const getRoleColor = (roleId) => {
        switch(roleId) {
            case 'AD': return 'primary';
            case 'ST': return 'info';
            default: return 'default';
        }
    };

    // Function to get branch name from branch ID
    const getBranchName = (branchId) => {
        if (!branchId || branchId === '') return "Not Assigned";
        const branch = branches.find(item => item.id.toString() === branchId.toString());
        return branch ? branch.name : branchId;
    };

    // Function to get branch color
    const getBranchColor = (branchId) => {
        if (!branchId || branchId === '') return "default";
        const branchIndex = branches.findIndex(item => item.id.toString() === branchId.toString());
        const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
        return colors[branchIndex % colors.length] || 'default';
    };

    // Function to get status display text
    const getStatusText = (status) => {
        return status === '1' || status === 1 ? 'Active' : 'Inactive';
    };

    // Function to get status color
    const getStatusColor = (status) => {
        return status === '1' || status === 1 ? 'success' : 'error';
    };

    // Function to get status icon
    const getStatusIcon = (status) => {
        return status === '1' || status === 1 ? <CheckCircle /> : <Cancel />;
    };

    // Fetch branches from API
    const fetchBranches = async () => {
        setBranchesLoading(true);
        setBranchesError(null);

        try {
            const result = await apiService.listNames(APIBranchNames);
            
            if (result.success) {
                const fetchedBranches = Array.isArray(result.data) ? result.data : [];
                setBranches(fetchedBranches);
            } else {
                setBranchesError("Failed to load branches");
                console.error("Branch fetch error:", result.message);
            }
        } catch (error) {
            let errorMessage = "Failed to load branches";
            if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setBranchesError(errorMessage);
            console.error("Branch API Error:", error);
        } finally {
            setBranchesLoading(false);
        }
    };

    // Fetch users using ApiService
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIUserList);
            
            if (result.success) {
                const users = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: users
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const roleName = getRoleName(record.role);
        const branchName = getBranchName(record.branch_id);
        
        const matchesSearch = searchTerm === '' ||
            record.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            branchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getStatusText(record.status).toLowerCase().includes(searchTerm.toLowerCase());

        // Name filter
        const matchesName = !filters.name ||
            record.name?.toLowerCase().includes(filters.name.toLowerCase());
        
        const matchesEmail = !filters.email ||
            record.email?.toLowerCase().includes(filters.email.toLowerCase());
        
        const matchesRole = !filters.role ||
            record.role === filters.role;

        const matchesBranch = !filters.branch_id ||
            record.branch_id?.toString() === filters.branch_id.toString();

        // Status filter
        const matchesStatus = !filters.status ||
            record.status?.toString() === filters.status;

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom && record.created_at) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo && record.created_at) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesEmail && matchesRole && matchesBranch && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add users", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            name: '',
            email: '',
            role: '',
            branch_id: '',
            password: '',
            confirmPassword: '',
            status: '1'
        });
        setOpenAdd(true);
    }

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit users", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            name: record.name || '',
            email: record.email || '',
            role: record.role || '',
            branch_id: record.branch_id || getUserDetails("branchId") || '',
            password: '',
            confirmPassword: '',
            status: record.status?.toString() || '1'
        });
        
        setOpenAdd(true);
    };

    const handleOpenChangePassword = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to change user passwords", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPasswordFormData({
            password: '',
            confirmPassword: ''
        });
        setOpenChangePassword(true);
    };

    const handleCloseChangePassword = () => {
        setOpenChangePassword(false);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete users", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;

        setPasswordErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setPasswordFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Toggle status handler
    const handleStatusToggle = () => {
        const newStatus = formData.status === '1' ? '0' : '1';
        setFormData(prev => ({
            ...prev,
            status: newStatus
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            email: '',
            role: '',
            branch_id: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const validateForm = () => {
        const errorsList = [];
        if (!formData.name?.trim()) errorsList.push('Name is required');
        if (!formData.email?.trim()) errorsList.push('Email is required');
        if (!formData.role?.trim()) errorsList.push('Role is required');
        if (!formData.branch_id || formData.branch_id === '') {
            errorsList.push('Branch is required');
        }

        // For new users, password is required
        if (postStatus === "ADD" && !formData.password?.trim()) {
            errorsList.push('Password is required for new users');
        }

        // Password confirmation
        if (formData.password && formData.password !== formData.confirmPassword) {
            errorsList.push('Passwords do not match');
        }

        if (formData.password && formData.password.length < 4) {
            errorsList.push('Password must be at least 4 characters long');
        }

        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            errorsList.push('Please enter a valid email address');
        }

        if (errorsList.length > 0) {
            showToast("Validation Error", errorsList.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const validatePasswordForm = () => {
        const errorsList = [];
        if (!passwordFormData.password?.trim()) errorsList.push('Password is required');
        if (passwordFormData.password !== passwordFormData.confirmPassword) {
            errorsList.push('Passwords do not match');
        }
        if (passwordFormData.password?.length < 4) {
            errorsList.push('Password must be at least 4 characters long');
        }

        if (errorsList.length > 0) {
            showToast("Validation Error", errorsList.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setOpenChangePassword(false);
        setFilterOptions(false);
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            email: '',
            role: '',
            branch_id: getUserDetails("branchId") || '',
            password: '',
            confirmPassword: '',
            status: '1'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add users", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update users", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete users", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));

        if (postStatus === "DELETE") {
            formDataToSend.append('branch_id', selectedRecord?.branch_id || getUserDetails("branchId"));
            formDataToSend.append('updateId', updateId);
        } else {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('role', formData.role);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('branch_id', formData.branch_id);

            // Only append password if it is provided
            if (formData.password) {
                formDataToSend.append('password', formData.password);
            }

            if (postStatus === "UPDATE") {
                formDataToSend.append('updateId', updateId);
            }
        }

        try {
            let result;
            
            if (postStatus === "ADD") {
                result = await apiService.create(APIUserStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIUserUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIUserDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleFormDataReset();
                handleDialogClose();
                fetchData();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePasswordForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('email', selectedRecord.email);
        formDataToSend.append('password', passwordFormData.password);
        formDataToSend.append('branch_id', selectedRecord.branch_id);
        formDataToSend.append('user_id', getUserDetails("Id"));

        try {
            const result = await apiService.update(APIUserUpdatePassword, formDataToSend);
            
            if (result.success) {
                showToast("Success", result.message, "success");
                handleDialogClose();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    if (permissionLoading) {
        return <div>Loading permissions...</div>;
    }

    if (loading || branchesLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (branchesError) return <div>Error loading branches: {branchesError}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Users Management ( {formatNumberWithComma(pageState.pageData.length)} Total )
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Add New User
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                View Options
                            </Button>
                        )}
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.main
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {(filters.name || filters.email || filters.role || filters.branch_id || filters.status || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.email && (
                            <Chip
                                label={`Email: ${filters.email}`}
                                onDelete={() => setFilters(prev => ({ ...prev, email: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.role && (
                            <Chip
                                label={`Role: ${getRoleName(filters.role)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, role: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.branch_id && (
                            <Chip
                                icon={<StoreIcon />}
                                label={`Branch: ${getBranchName(filters.branch_id)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, branch_id: '' }))}
                                color={getBranchColor(filters.branch_id)}
                                variant="outlined"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusText(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color={getStatusColor(filters.status)}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Branch</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => (
                                    <TableRow
                                        key={user.id || index}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(user.created_at)}</TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            {user.name}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getRoleName(user.role)}
                                                color={getRoleColor(user.role)}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {user.branch_id ? (
                                                <Chip
                                                    icon={<StoreIcon />}
                                                    label={getBranchName(user.branch_id)}
                                                    color={getBranchColor(user.branch_id)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(user.status)}
                                                label={getStatusText(user.status)}
                                                color={getStatusColor(user.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenView(user)}
                                                        color="primary"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.info.light
                                                            }
                                                        }}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {hasPermission('EDIT') && (
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenEdit(user)}
                                                            color="secondary"
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.success.light
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Change Password">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenChangePassword(user)}
                                                            color="warning"
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.warning.light
                                                                }
                                                            }}
                                                        >
                                                            <KeyIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            
                                            {hasPermission('DELETE') && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenDelete(user)}
                                                        color="error"
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.error.light
                                                            }
                                                        }}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No users found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Users
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Users
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Name / Email
                        </Typography>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={filters.email}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Role
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                name="role"
                                value={filters.role}
                                onChange={handleFilterChange}
                                label="Role"
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                {localRoleOptions.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Branch
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                name="branch_id"
                                value={filters.branch_id}
                                onChange={handleFilterChange}
                                label="Branch"
                                disabled={branchesLoading}
                            >
                                <MenuItem value="">All Branches</MenuItem>
                                {branches.map((branch, index) => (
                                    <MenuItem key={index} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New User" : "Edit User"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.name}
                                    helperText={errors.name ? "Name is required" : ""}
                                    required
                                />
                            </Grid><br/>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    error={errors.email}
                                    helperText={errors.email ? "Valid email is required" : ""}
                                    required
                                />
                            </Grid><br/>
                            
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        name="role"
                                        value={formData.role}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        label="Role"
                                        required
                                    >
                                        <MenuItem value="">Select Role</MenuItem>
                                        {localRoleOptions.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Branch</InputLabel>
                                    <Select
                                        name="branch_id"
                                        value={formData.branch_id}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        label="Branch"
                                        required
                                        disabled={branchesLoading}
                                    >
                                        <MenuItem value="">Select Branch</MenuItem>
                                        {branches.map((branch, index) => (
                                            <MenuItem key={index} value={branch.id}>
                                                {branch.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body1">Status:</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={formData.status === '1' ? 'Active' : 'Inactive'}
                                                color={formData.status === '1' ? 'success' : 'error'}
                                                size="small"
                                            />
                                            <Switch
                                                checked={formData.status === '1'}
                                                onChange={handleStatusToggle}
                                                color={formData.status === '1' ? 'success' : 'error'}
                                            />
                                        </Box>
                                    </Box>
                                </FormControl>
                            </Grid><br/>

                            {postStatus === "ADD" && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            error={errors.password}
                                            helperText={errors.password ? "Password is required (min 4 characters)" : ""}
                                            required
                                        />
                                    </Grid><br/>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Confirm Password"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            error={errors.confirmPassword}
                                            helperText={errors.confirmPassword ? "Passwords must match" : ""}
                                            required
                                        />
                                    </Grid>
                                </>
                            )}

                            {postStatus === "UPDATE" && (
                                <>
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 1 }}>
                                            Leave password fields empty to keep current password
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="New Password (Optional)"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            helperText="Leave blank to keep current password"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Confirm New Password"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            autoComplete="new-password"
                                            onChange={handleInputChange}
                                            helperText="Confirm new password if changed"
                                        />
                                    </Grid>
                                </>
                            )}
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseAdd}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={branchesLoading}
                            >
                                {postStatus === "ADD" ? "Add User" : "Update User"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog
                open={openChangePassword}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseChangePassword();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Change Password for User</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                Changing password for user with email: <b>{selectedRecord?.email}</b>
                            </Typography>
                        </Alert>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    name="password"
                                    type="password"
                                    value={passwordFormData.password}
                                    autoComplete="new-password"
                                    onChange={handlePasswordInputChange}
                                    error={passwordErrors.password}
                                    helperText="Minimum 4 characters required"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type="password"
                                    value={passwordFormData.confirmPassword}
                                    autoComplete="new-password"
                                    onChange={handlePasswordInputChange}
                                    error={passwordErrors.confirmPassword}
                                    helperText="Must match the password above"
                                    required
                                />
                            </Grid>
                        </Grid>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseChangePassword}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={!passwordFormData.password || !passwordFormData.confirmPassword}
                            >
                                Update Password
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>User Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Name: </b>{selectedRecord.name}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Email: </b>{selectedRecord.email}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Role: </b>
                                        <Chip
                                            label={getRoleName(selectedRecord.role)}
                                            color={getRoleColor(selectedRecord.role)}
                                            variant="outlined"
                                        />
                                    </Typography>
                                </Grid><br/>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Branch: </b>
                                        {selectedRecord.branch_id ? (
                                            <Chip
                                                icon={<StoreIcon />}
                                                label={getBranchName(selectedRecord.branch_id)}
                                                color={getBranchColor(selectedRecord.branch_id)}
                                                variant="outlined"
                                                size="small"
                                            />
                                        ) : (
                                            'Not Assigned'
                                        )}
                                    </Typography>
                                </Grid><br/>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Status: </b>
                                        <Chip
                                            icon={getStatusIcon(selectedRecord.status)}
                                            label={getStatusText(selectedRecord.status)}
                                            color={getStatusColor(selectedRecord.status)}
                                            size="small"
                                        />
                                    </Typography>
                                </Grid><br/>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Created Date: </b>{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid><br/>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Last Updated: </b>{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete User</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this user?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" component="div" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name} <br /> 
                            <Typography variant="body2" color="text.secondary">
                                {selectedRecord.email}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={getRoleName(selectedRecord.role)}
                                    color={getRoleColor(selectedRecord.role)}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                {selectedRecord.branch_id && (
                                    <Chip
                                        icon={<StoreIcon />}
                                        label={getBranchName(selectedRecord.branch_id)}
                                        color={getBranchColor(selectedRecord.branch_id)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mr: 1 }}
                                    />
                                )}
                                <Chip
                                    icon={getStatusIcon(selectedRecord.status)}
                                    label={getStatusText(selectedRecord.status)}
                                    color={getStatusColor(selectedRecord.status)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Users;